import React from 'react'
import { Form } from 'react-bulma-components'
import FieldColumn from '../field-column'
import { IsoDate } from '../../../hooks/use-calendar'
import { dateToDateInputValue, endOfWeek, startOfWeek } from '../../../utils/date'

const MS_PER_DAY = 86_400_000

interface WeekRangeFilterProps {
  fromValue: IsoDate | ''
  toValue: IsoDate | ''
  onFromChange: (value: IsoDate | '') => void
  onToChange: (value: IsoDate | '') => void
  /** Earliest selectable date (already aligned to start-of-week) */
  minDate?: Date
  /** Latest selectable date (already aligned to end-of-week). Omit for open-ended missions (CDI). */
  maxDate?: Date
  /** Maximum allowed span between from and to, in days. Omit for unlimited range. */
  maxRangeDays?: number
}

/**
 * Controlled week-range filter with two date inputs ("Du" / "Au").
 * Automatically cross-validates: if `from > to`, the other bound is adjusted.
 * When `maxRangeDays` is set, the selectable range and HTML min/max attributes are
 * dynamically constrained so the user cannot exceed the allowed span.
 * Renders two `FieldColumn`s — the parent is responsible for layout (e.g. Level).
 */
const WeekRangeFilter: React.FC<WeekRangeFilterProps> = ({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  minDate,
  maxDate,
  maxRangeDays,
}) => {
  const maxRangeMs = maxRangeDays ? maxRangeDays * MS_PER_DAY : undefined

  /** Clamp `date` between optional lower/upper bounds. */
  const clampDate = (date: Date, min?: Date | null, max?: Date | null): Date => {
    if (min && date < min) return min
    if (max && date > max) return max
    return date
  }

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = (e.target.value || '') as IsoDate
    onFromChange(v)
    if (!v) return
    const fromW = startOfWeek(new Date(v))
    let toW = endOfWeek(new Date(toValue || v))
    if (fromW > toW) {
      toW = endOfWeek(fromW)
    }
    if (maxRangeMs && toW.getTime() - fromW.getTime() > maxRangeMs) {
      toW = clampDate(endOfWeek(new Date(fromW.getTime() + maxRangeMs)), null, maxDate)
    }
    if (toW.getTime() !== endOfWeek(new Date(toValue || v)).getTime()) {
      onToChange(dateToDateInputValue(toW))
    }
  }

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = (e.target.value || '') as IsoDate
    onToChange(v)
    if (!v) return
    const toW = endOfWeek(new Date(v))
    let fromW = startOfWeek(new Date(fromValue || v))
    if (fromW > toW) {
      fromW = startOfWeek(toW)
    }
    if (maxRangeMs && toW.getTime() - fromW.getTime() > maxRangeMs) {
      fromW = clampDate(startOfWeek(new Date(toW.getTime() - maxRangeMs)), minDate)
    }
    if (fromW.getTime() !== startOfWeek(new Date(fromValue || v)).getTime()) {
      onFromChange(dateToDateInputValue(fromW))
    }
  }

  /**
   * Dynamic min/max per input:
   * - "from" min is clamped up to `to - maxRange` so the user can't pick a date too far back
   * - "to" max is clamped down to `from + maxRange` so the user can't pick a date too far ahead
   */
  const globalMinStr = minDate ? dateToDateInputValue(minDate) : undefined
  const globalMaxStr = maxDate ? dateToDateInputValue(maxDate) : undefined

  let fromMinStr = globalMinStr
  if (maxRangeMs && toValue) {
    const rangeMin = startOfWeek(new Date(new Date(toValue).getTime() - maxRangeMs))
    const rangeMinStr = dateToDateInputValue(clampDate(rangeMin, minDate))
    fromMinStr = fromMinStr && fromMinStr > rangeMinStr ? fromMinStr : rangeMinStr
  }

  let toMaxStr = globalMaxStr
  if (maxRangeMs && fromValue) {
    const rangeMax = endOfWeek(new Date(new Date(fromValue).getTime() + maxRangeMs))
    const rangeMaxStr = dateToDateInputValue(clampDate(rangeMax, null, maxDate))
    toMaxStr = toMaxStr && toMaxStr < rangeMaxStr ? toMaxStr : rangeMaxStr
  }

  return (
    <>
      <FieldColumn>
        <Form.Field>
          <Form.Label>Semaine du</Form.Label>
          <Form.Control>
            <Form.Input
              type="date"
              value={fromValue}
              onChange={handleFromChange}
              min={fromMinStr}
              max={globalMaxStr}
              name="weekRangeFrom"
            />
          </Form.Control>
        </Form.Field>
      </FieldColumn>
      <FieldColumn>
        <Form.Field>
          <Form.Label>Au</Form.Label>
          <Form.Control>
            <Form.Input
              type="date"
              value={toValue}
              onChange={handleToChange}
              min={globalMinStr}
              max={toMaxStr}
              name="weekRangeTo"
            />
          </Form.Control>
        </Form.Field>
      </FieldColumn>
    </>
  )
}

export default WeekRangeFilter
